import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Stack,
    Switch,
    FormControlLabel,
    Paper,
    Link
} from "@mui/material";

// Initilized the storage variable to get and set cookies
const STORAGE_KEY = "deskDynamicsCookieConsent";

// Default Cookies State (functional, statistics, marketing)
const defaultState = {
    functional: true,
    statistics: false,
    marketing: false
};

export default function CookieBanner() {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(defaultState);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (!saved) {
            setOpen(true);
        } else {
            const parsed = JSON.parse(saved);
            setState(parsed);
        }
    }, []);

    const saveAndClose = (next) => {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        setState(next);
        setOpen(false);
    };

    const handleAcceptAll = () => {
        saveAndClose({ functional: true, statistics: true, marketing: true });
    };

    const handleRejectAll = () => {
        saveAndClose({ functional: true, statistics: false, marketing: false });
    };

    const handleSaveSelection = () => {
        saveAndClose({ ...state });
    };

    if (!open) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 24,
                px: 2,
                zIndex: 1300
            }}
        >
            <Paper
                elevation={6}
                sx={(theme) => ({
                    maxWidth: 900,
                    mx: "auto",
                    borderRadius: 3,
                    p: 3,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                    alignItems: "stretch",
                    background: "linear-gradient(135deg,#050827,#05001B)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: theme.palette.common.white
                })}
            >
                <Stack spacing={1} flex={1}>
                    <Typography variant="h6" fontWeight={700}>
                        We Value Your Privacy
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        We use cookies to ensure basic functionality and enhance your experience.
                        You can customize your preferences below. Only functional cookies are
                        required to use our services.
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        By clicking "Accept All", you consent to statistics and marketing cookies.
                        You can still use our application if you choose "Only Necessary".
                        Read our{" "}
                        <Link href="https://deskdynamics.com/cookie-richtlinie-eu/" color="primary" underline="hover">
                            {" "}Cookie Policy{" "}
                        </Link>
                        {" "}for details.
                    </Typography>

                    {showDetails && (
                        <Stack spacing={0.5} mt={1}>
                            <FormControlLabel
                                control={<Switch checked />}
                                label="Functional-Cookies (Necessary)"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={state.statistics}
                                        onChange={(_, checked) =>
                                            setState((s) => ({ ...s, statistics: checked }))
                                        }
                                    />
                                }
                                label="Statistics-Cookies"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={state.marketing}
                                        onChange={(_, checked) =>
                                            setState((s) => ({ ...s, marketing: checked }))
                                        }
                                    />
                                }
                                label="Marketing-Cookies"
                            />
                        </Stack>
                    )}
                </Stack>

                <Stack
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "flex-end" }}
                    minWidth={{ md: 260 }}
                >
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        width="100%"
                    >
                        {/* Only necessary button */}
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleRejectAll}
                            sx={{
                                borderRadius: 999,
                                borderColor: "rgba(255,255,255,0.5)",
                                color: "#fff",
                                textTransform: "none",
                                fontWeight: 600
                            }}
                        >
                            Only necessary
                        </Button>
                        {/* Accept all button */}
                        <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            onClick={handleAcceptAll}
                            sx={{
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 700
                            }}
                        >
                            Accept All
                        </Button>
                    </Stack>
                    
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => setShowDetails((v) => !v)}
                        sx={{
                            textTransform: "none",
                            fontWeight: 500,
                            alignSelf: { xs: "flex-start", md: "flex-end" }
                        }}
                    >
                        {showDetails ? "Save selection" : "View settings"}
                    </Button>

                    {showDetails && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveSelection}
                            sx={{
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 600,
                                mt: -0.5
                            }}
                            fullWidth
                        >
                            Save selection
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
}
